import { Controller } from "@hotwired/stimulus"

const GOOGLE_CLIENT_SRC = "https://accounts.google.com/gsi/client"
const APPLE_CLIENT_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"

let googleScriptPromise
let appleScriptPromise

export default class extends Controller {
  static targets = ["appleButton", "error", "googleButton"]
  static values = {
    appleClientId: String,
    appleNonce: String,
    appleRedirectUrl: String,
    appleState: String,
    oauthPath: String,
    googleClientId: String,
    googleNonce: String
  }

  connect() {
    this.initializeGoogle()
    this.initializeApple()
  }

  async initializeGoogle() {
    if (!this.hasGoogleClientIdValue || !this.hasGoogleButtonTarget) return

    try {
      await this.loadGoogleScript()

      if (!window.google?.accounts?.id) {
        throw new Error("Google Sign-In is unavailable")
      }

      window.google.accounts.id.initialize({
        client_id: this.googleClientIdValue,
        nonce: this.googleNonceValue,
        callback: (response) => {
          void this.submitGoogleCredential(response?.credential)
        }
      })

      this.googleButtonTarget.innerHTML = ""
      window.google.accounts.id.renderButton(this.googleButtonTarget, {
        theme: "outline",
        size: "large",
        width: 320,
        text: "signin_with",
        shape: "pill"
      })
    } catch (_error) {
      this.showError("Google sign-in is unavailable right now.")
    }
  }

  async initializeApple() {
    if (!this.hasAppleClientIdValue || !this.hasAppleButtonTarget) return

    try {
      await this.loadAppleScript()

      if (!window.AppleID?.auth) {
        throw new Error("Apple Sign-In is unavailable")
      }

      window.AppleID.auth.init({
        clientId: this.appleClientIdValue,
        scope: "name email",
        redirectURI: this.appleRedirectUrlValue,
        state: this.appleStateValue,
        nonce: this.appleNonceValue,
        usePopup: false
      })
    } catch (_error) {
      this.showError("Apple sign-in is unavailable right now.")
    }
  }

  signInWithApple(event) {
    event.preventDefault()
    this.clearError()

    if (!window.AppleID?.auth) {
      this.showError("Apple sign-in is unavailable right now.")
      return
    }

    window.AppleID.auth.signIn().catch(() => {
      this.showError("Apple sign-in could not be completed. Please try again.")
    })
  }

  async submitGoogleCredential(credential) {
    if (!credential) {
      this.showError("Google sign-in could not be completed. Please try again.")
      return
    }

    this.clearError()

    try {
      const response = await fetch(this.oauthPathValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({
          provider: "google",
          id_token: credential
        })
      })

      const payload = await response.json().catch(() => ({}))

      if (response.ok && payload.redirect_url) {
        window.location.assign(payload.redirect_url)
        return
      }

      if (payload.next_nonce) {
        this.googleNonceValue = payload.next_nonce
        this.initializeGoogle()
      }

      this.showError(payload.error || "Google sign-in could not be completed. Please try again.")
    } catch (_error) {
      this.showError("Google sign-in could not be completed. Please try again.")
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  loadGoogleScript() {
    googleScriptPromise ||= this.loadExternalScript(GOOGLE_CLIENT_SRC)
    return googleScriptPromise
  }

  loadAppleScript() {
    appleScriptPromise ||= this.loadExternalScript(APPLE_CLIENT_SRC)
    return appleScriptPromise
  }

  loadExternalScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`)
      if (existing) {
        if (existing.dataset.loaded == "true") {
          resolve()
        } else {
          existing.addEventListener("load", () => resolve(), { once: true })
          existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true })
        }
        return
      }

      const script = document.createElement("script")
      script.src = src
      script.async = true
      script.defer = true
      script.addEventListener("load", () => {
        script.dataset.loaded = "true"
        resolve()
      }, { once: true })
      script.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true })
      document.head.appendChild(script)
    })
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
